"use client";

import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { LinkIcon } from "@phosphor-icons/react/dist/csr/Link";
import { ShieldCheckIcon } from "@phosphor-icons/react/dist/csr/ShieldCheck";
import { WalletIcon } from "@phosphor-icons/react/dist/csr/Wallet";
import { Badge } from "design-system/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "design-system/dialog";
import { cn } from "design-system/lib/utils";
import { Spinner } from "design-system/spinner";
import { WarningBanner } from "@/components/custom/warning-banner";
import { useAdditionalEmbeddedWalletAccountsEnabled } from "@/lib/hooks/flags/use-additional-embedded-wallet-accounts-enabled";
import type { UseDialogReturn } from "@/lib/hooks/use-dialog";
import { useEmbeddedWalletAtMax } from "@/subsystems/embedded-wallets/use-embedded-wallet-at-max";

// Optional per-trigger override. A caller can pass `onSelectExternal` to keep its own external-wallet flow
// (e.g. the onboarding task card navigates to the /add page) instead of the default external add-address dialog.
export type AddBlockchainAddressChooserData = {
  onSelectExternal?: () => void;
};

// Entry chooser for adding a blockchain address: create an Agora (Turnkey) embedded wallet, or register an
// external one. The two targets are passed in as controls so this stays decoupled from the dialog registry.
type Props = {
  agoraWalletControls: UseDialogReturn<void>;
  dialogControls: UseDialogReturn<AddBlockchainAddressChooserData>;
  externalWalletControls: UseDialogReturn<void>;
};

export function AddBlockchainAddressChooserDialog({
  agoraWalletControls,
  dialogControls,
  externalWalletControls,
}: Props) {
  const additionalAccountsEnabled = useAdditionalEmbeddedWalletAccountsEnabled();
  // Fetch the account count only while open AND the additional-accounts flag is on. This dialog is
  // mounted unconditionally by the protected layout; with the flag off the limit doesn't apply, so an
  // always-on call would spin/block the Agora option for orgs prod never does that to.
  const { atMax, isError, isLoading } = useEmbeddedWalletAtMax(
    dialogControls.props.open && additionalAccountsEnabled === true,
  );
  // Hold the Agora option until the account count is known. On error, fail closed: don't let an
  // org we can't verify start creating an account it may not have room for.
  const addAgoraAccountDisabled = atMax || isError || isLoading;

  function chooseAgora() {
    if (addAgoraAccountDisabled) return;
    dialogControls.dismiss();
    agoraWalletControls.trigger();
  }

  function chooseExternal() {
    const onSelectExternal = dialogControls.data?.onSelectExternal;
    dialogControls.dismiss();
    if (onSelectExternal) {
      onSelectExternal();
    } else {
      externalWalletControls.trigger();
    }
  }

  return (
    <Dialog {...dialogControls.props}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader className="text-left">
          <DialogTitle>Add blockchain address</DialogTitle>
          <DialogDescription className="text-neutral-600">Choose how you want to add an address.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <button
            className={cn(
              "w-full rounded-lg bg-neutral-100 p-4 text-left transition-colors",
              addAgoraAccountDisabled ? "pointer-events-none" : "hover:bg-neutral-200/60",
            )}
            aria-disabled={addAgoraAccountDisabled}
            data-testid="select-agora-wallet-btn"
            onClick={chooseAgora}
            type="button"
          >
            <span className="flex w-full items-start gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-neutral-200">
                <WalletIcon className="size-5" />
              </span>
              <span className="flex-1 space-y-1">
                <span className="flex items-center gap-2">
                  <span className="font-medium">Agora wallet</span>
                  <Badge className="bg-neutral-200 text-neutral-700" variant="secondary">
                    <ShieldCheckIcon />
                    Recommended
                  </Badge>
                </span>
                <span className="block text-sm text-neutral-600">
                  {"A self-custodial wallet your organization creates and controls inside Agora. You'll hold the keys."}
                </span>
              </span>
              {isLoading ? (
                <Spinner size="inside-button" />
              ) : (
                !addAgoraAccountDisabled && <CaretRightIcon className="size-4 shrink-0 text-neutral-500" />
              )}
            </span>
            {atMax && (
              <WarningBanner
                className="mt-2"
                data-testid="agora-wallet-full-warning"
                description="This wallet has all the accounts it can hold for now. Reach out to Agora if you need more, or rename an account to give it a new purpose."
                variant="info"
              />
            )}
            {isError && (
              <span
                className="mt-2 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                data-testid="agora-wallet-check-error"
              >
                <InfoIcon aria-hidden className="mt-0.5 shrink-0" size={16} />
                <span className="min-w-0">We couldn't check your wallet right now. Try again shortly.</span>
              </span>
            )}
          </button>

          <button
            className="flex w-full items-center gap-3 rounded-lg bg-neutral-100 p-4 text-left transition-colors hover:bg-neutral-200/60"
            data-testid="select-external-wallet-btn"
            onClick={chooseExternal}
            type="button"
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-neutral-200">
              <LinkIcon className="size-5" />
            </span>
            <span className="flex-1 space-y-1">
              <span className="block font-medium">External wallet</span>
              <span className="block text-sm text-neutral-600">
                Register an address you already control, like Fireblocks or MetaMask. Screened before it can be used.
              </span>
            </span>
            <CaretRightIcon className="size-4 shrink-0 text-neutral-500" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
